import { API_BASE_URL } from '../config';

// Thrown by getZip when the postal code is unknown (HTTP 404).
export class NotFoundError extends Error {
  constructor(postalCode) {
    super(`postal code not found: ${postalCode}`);
    this.name = 'NotFoundError';
    this.notFound = true;
    this.postalCode = postalCode;
  }
}

async function getJson(path) {
  const res = await fetch(`${API_BASE_URL}${path}`);
  if (!res.ok) {
    const err = new Error(`request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// GET /zip/:zipcode  -> { postal_code, entries: [...] }
export async function getZip(zipcode) {
  const code = String(zipcode || '').replace(/[-\s]/g, '');
  const res = await fetch(`${API_BASE_URL}/zip/${encodeURIComponent(code)}`);
  if (res.status === 404) throw new NotFoundError(code);
  if (!res.ok) {
    const err = new Error(`request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// GET /reverse/:query -> { query, results: [...], total, truncated }
// (200 + empty results if none). `results` is server-capped; `truncated` is true
// when total > results.length, signalling the query was too broad.
export async function getReverse(query) {
  return getJson(`/reverse/${encodeURIComponent(String(query || '').trim())}`);
}

// GET /breakdown?pref=&city=&ward=&town=
// Pass only the levels selected so far (in order). The first omitted level's
// `options` come back; pass all four to get `results`. Note: passing a level
// with an empty string ("") is a real level (e.g. Tokyo special wards), NOT the
// same as omitting it — so we only append keys that are present in `levels`.
export async function getBreakdown(levels = {}) {
  const order = ['pref', 'city', 'ward', 'town'];
  const params = new URLSearchParams();
  for (const key of order) {
    if (Object.prototype.hasOwnProperty.call(levels, key)) {
      params.append(key, levels[key]);
    }
  }
  const qs = params.toString();
  const data = await getJson(`/breakdown${qs ? `?${qs}` : ''}`);
  if (Array.isArray(data.options)) {
    data.options = data.options.map(normalizeOption);
  }
  return data;
}

// `/breakdown` options may arrive as { value, label } objects (value = kanji name
// to drill down on, label = romaji for display) or, from an older API, as plain
// kanji strings. Normalize to the object shape so the UI can rely on it; when no
// romaji label is available it falls back to the kanji value.
function normalizeOption(opt) {
  if (opt && typeof opt === 'object') {
    const value = opt.value ?? '';
    return { value, label: opt.label ?? value, katakana: opt.katakana ?? '' };
  }
  const value = opt ?? '';
  return { value, label: value, katakana: '' };
}

// GET /meta -> { dataLastModified }. Cached for the page's lifetime so the
// footer fetches it once regardless of route changes.
let _metaPromise = null;
export function getMeta() {
  if (!_metaPromise) {
    _metaPromise = getJson('/meta').catch((e) => {
      _metaPromise = null; // allow retry on next mount after a failure
      throw e;
    });
  }
  return _metaPromise;
}
